import { useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/hooks/useOrganization";
import { Database } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { MultiFieldTextQuestion } from "./questionTypes";

type OrganizationInsert = Database['public']['Tables']['organizations']['Insert'];
import { MultiFieldTextQuestionComponent } from "./MultiFieldTextQuestion";
import { ProfileField } from "./components/ProfileField";
import { ProfileFieldDropdown } from "./components/ProfileFieldDropdown";
import { ProfileFieldBalanced } from "./components/ProfileFieldBalanced";
import { ProfileFieldDropdownBalanced } from "./components/ProfileFieldDropdownBalanced";
import { BusinessProfileLoadingSkeleton } from "./components/BusinessProfileLoadingSkeleton";
import { SaveStatusIndicator } from "./components/SaveStatusIndicator";
import "./components/BalancedProfileStyles.css";
import { logger } from '@/utils/logger';
interface BusinessProfileData {
  legalEntityName?: string;
  dbaName?: string;
  businessStructure?: string;
  industry?: string;
  website?: string;
  sourceTable?: 'organizations' | 'profiles' | 'none' | 'error';
  organizationId?: string;
  lastUpdated?: string;
}
interface BusinessDetailsSummaryQuestionProps {
  question: MultiFieldTextQuestion;
  values: Record<string, string>;
  onChange: (questionId: string, values: Record<string, string>) => void;
}
export function BusinessDetailsSummaryQuestion({
  question,
  values,
  onChange
}: BusinessDetailsSummaryQuestionProps) {
  const [profileData, setProfileData] = useState<BusinessProfileData>({});
  const [editingField, setEditingField] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const {
    user
  } = useAuth();
  const {
    organization,
    updateOrganization
  } = useOrganization();
  const [profile, setProfile] = useState<Database['public']['Tables']['profiles']['Row'] | null>(null);

  // Only apply this enhanced behavior to business-details question
  const isBusinessDetailsQuestion = question.id === 'business-details';
  if (!isBusinessDetailsQuestion) {
    // Fall back to original MultiFieldTextQuestionComponent for other questions
    return <MultiFieldTextQuestionComponent question={question} values={values} onChange={onChange} />;
  }

  // Load existing data on component mount
  useEffect(() => {
    loadBusinessProfileData();
  }, [organization, profile]);

  // Fetch profile data separately
  useEffect(() => {
    if (user?.id) {
      fetchProfile();
    }
  }, [user?.id]);
  const fetchProfile = async () => {
    if (!user?.id) return;
    try {
      const {
        data,
        error
      } = await supabase.from('profiles').select('*').eq('user_id', user.id).single();
      if (error && error.code !== 'PGRST116') {
        // PGRST116 is "not found"
        throw error;
      }
      setProfile(data);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'profile_fetch', component: 'BusinessDetailsSummaryQuestion' },
        extra: { message: 'Failed to fetch profile', userId: user?.id }
      });
    }
  };
  const loadBusinessProfileData = async () => {
    setLoading(true);
    try {
      const data = await fetchBusinessProfileData();
      setProfileData(data);

      // If we have existing data, mark it as the current answer
      if (data.sourceTable && data.sourceTable !== 'none' && data.sourceTable !== 'error') {
        onChange(question.id, {
          legalEntityName: data.legalEntityName || '',
          dbaName: data.dbaName || '',
          businessStructure: data.businessStructure || '',
          industry: data.industry || '',
          website: data.website || ''
        });
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'business_profile_load', component: 'BusinessDetailsSummaryQuestion' },
        extra: { message: 'Failed to load business profile data' }
      });
      setSaveStatus('error');
    } finally {
      setLoading(false);
    }
  };
  const fetchBusinessProfileData = async (): Promise<BusinessProfileData> => {
    try {
      // First, try to get data from user's organization
      if (organization) {
        return {
          legalEntityName: organization.name,
          dbaName: '',
          // Not stored yet - would need new field
          businessStructure: organization.business_type,
          industry: organization.industry,
          website: organization.website,
          sourceTable: 'organizations',
          organizationId: organization.id,
          lastUpdated: organization.updated_at
        };
      }

      // Fallback to profiles table if no organization data
      if (profile) {
        return {
          legalEntityName: profile.company_name || '',
          dbaName: '',
          // Not available in profiles
          businessStructure: profile.business_structure || '',
          industry: profile.industry || '',
          website: '',
          // Not available in profiles
          sourceTable: 'profiles',
          lastUpdated: profile.updated_at
        };
      }

      // No data found
      return {
        sourceTable: 'none'
      };
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'business_profile_fetch', component: 'BusinessDetailsSummaryQuestion' },
        extra: { message: 'Error fetching business profile data' }
      });
      return {
        sourceTable: 'error'
      };
    }
  };
  const handleFieldEdit = async (fieldName: string, newValue: string) => {
    setSaveStatus('saving');
    try {
      const updatedData = {
        ...profileData,
        [fieldName]: newValue
      };
      setProfileData(updatedData);
      setHasChanges(true);
      setEditingField(null);

      // Update the answer immediately for form state
      onChange(question.id, {
        legalEntityName: updatedData.legalEntityName || '',
        dbaName: updatedData.dbaName || '',
        businessStructure: updatedData.businessStructure || '',
        industry: updatedData.industry || '',
        website: updatedData.website || ''
      });

      // Save to database (except for DBA name until field is added)
      if (fieldName !== 'dbaName') {
        await saveFieldToDatabase(fieldName, newValue, updatedData);
      }
      setSaveStatus('saved');

      // Clear saved status after 2 seconds
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'field_save', component: 'BusinessDetailsSummaryQuestion' },
        extra: { message: 'Failed to save field', fieldName, newValue }
      });
      setSaveStatus('error');

      // Clear error status after 3 seconds
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };
  const saveFieldToDatabase = async (fieldName: string, value: string, profileData: BusinessProfileData) => {
    try {
      if (profileData.sourceTable === 'organizations' && profileData.organizationId) {
        // Update organizations table
        const dbFieldMap: Record<string, string> = {
          'legalEntityName': 'name',
          'businessStructure': 'business_type',
          'industry': 'industry',
          'website': 'website'
          // 'dbaName': 'trading_name' // Would need to add this field
        };
        const dbField = dbFieldMap[fieldName];
        if (!dbField) {
          logger.debug(`Field ${fieldName} not stored in organizations table`);
          return;
        }
        await updateOrganization({
          [dbField]: value
        });
        logger.debug(`Updated ${fieldName} in organizations table:`, value);
      } else {
        // Create new organization if none exists
        await createNewOrganization(fieldName, value);
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'database_update', component: 'BusinessDetailsSummaryQuestion', offline: !navigator.onLine },
        extra: { message: `Failed to update ${fieldName}`, fieldName, value }
      });
      
      if (!navigator.onLine) {
        setSaveStatus('saved'); // Show as saved, queued for offline sync
        toast.info("Saved Offline - Will sync when connection is restored");
        return;
      }
      
      setSaveStatus('error');
      throw error;
    }
  };
  const createNewOrganization = async (fieldName: string, value: string) => {
    if (!user?.id) return;
    try {
      // Create new organization with the provided data
      const orgData: OrganizationInsert = {
        name: fieldName === 'legalEntityName' ? value : '',
        business_type: fieldName === 'businessStructure' ? value : null,
        industry: fieldName === 'industry' ? value : null,
        website: fieldName === 'website' ? value : null
      };
      const {
        data: newOrg,
        error: orgError
      } = await supabase.from('organizations').insert(orgData).select().single();
      if (orgError) throw orgError;

      // Link user to organization
      const {
        error: linkError
      } = await supabase.from('user_organization_roles').insert({
        user_id: user.id,
        organization_id: newOrg.id,
        role: 'business_owner',
        assigned_by: user.id
      });
      if (linkError) throw linkError;
      logger.debug('Created new organization and linked to user');
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'organization_creation', component: 'BusinessDetailsSummaryQuestion', offline: !navigator.onLine },
        extra: { message: 'Failed to create new organization', userId: user?.id, fieldName, value }
      });
      
      if (!navigator.onLine) {
        setSaveStatus('saved'); // Show as saved, queued for offline sync
        toast.info("Saved Offline - Organization will be created when connection is restored");
        return;
      }
      
      setSaveStatus('error');
      throw error;
    }
  };
  const getBusinessStructureOptions = () => [{
    value: 'sole-proprietorship',
    label: 'Sole Proprietorship'
  }, {
    value: 'partnership',
    label: 'Partnership'
  }, {
    value: 'llc',
    label: 'Limited Liability Company (LLC)'
  }, {
    value: 'corporation',
    label: 'Corporation'
  }, {
    value: 's-corp',
    label: 'S Corporation'
  }, {
    value: 'professional-corp',
    label: 'Professional Corporation'
  }, {
    value: 'other',
    label: 'Other'
  }];
  const getIndustryOptions = () => [{
    value: 'technology',
    label: 'Technology'
  }, {
    value: 'healthcare',
    label: 'Healthcare'
  }, {
    value: 'finance',
    label: 'Finance & Banking'
  }, {
    value: 'retail',
    label: 'Retail'
  }, {
    value: 'manufacturing',
    label: 'Manufacturing'
  }, {
    value: 'construction',
    label: 'Construction'
  }, {
    value: 'professional-services',
    label: 'Professional Services'
  }, {
    value: 'food-beverage',
    label: 'Food & Beverage'
  }, {
    value: 'education',
    label: 'Education'
  }, {
    value: 'real-estate',
    label: 'Real Estate'
  }, {
    value: 'transportation',
    label: 'Transportation'
  }, {
    value: 'other',
    label: 'Other'
  }];
  if (loading) {
    return <BusinessProfileLoadingSkeleton />;
  }
  return <div className="business-details-summary-balanced">
      <div className="summary-header-balanced">
        <h2>Please confirm the information below — make any needed updates.</h2>
        <p className="summary-subtitle-balanced">
          We'll pre-fill what we have on file, but you can update anything that's changed.
        </p>
        
        {profileData.sourceTable === 'none' && <div className="no-data-notice-balanced">
            <p>
              We don't have business information on file yet. Please provide your details below.
            </p>
          </div>}
        
      </div>

      <div className="profile-fields-balanced">
        <ProfileFieldBalanced 
          label="Legal Entity Name" 
          value={profileData.legalEntityName} 
          isRequired={true} 
          isEditing={editingField === 'legalEntityName'} 
          onEdit={() => setEditingField('legalEntityName')} 
          onSave={value => handleFieldEdit('legalEntityName', value)} 
          onCancel={() => setEditingField(null)} 
          placeholder="Company legal name" 
          saveStatus={editingField === 'legalEntityName' ? saveStatus : 'idle'} 
        />

        <ProfileFieldBalanced 
          label="DBA/Trading Name" 
          value={profileData.dbaName} 
          isRequired={false} 
          isEditing={editingField === 'dbaName'} 
          onEdit={() => setEditingField('dbaName')} 
          onSave={value => handleFieldEdit('dbaName', value)} 
          onCancel={() => setEditingField(null)} 
          placeholder="Doing business as name (if different)" 
          saveStatus={editingField === 'dbaName' ? saveStatus : 'idle'} 
        />

        <ProfileFieldDropdownBalanced 
          label="Business Structure/Entity Type" 
          value={profileData.businessStructure} 
          options={getBusinessStructureOptions()} 
          isRequired={true} 
          isEditing={editingField === 'businessStructure'} 
          onEdit={() => setEditingField('businessStructure')} 
          onSave={value => handleFieldEdit('businessStructure', value)} 
          onCancel={() => setEditingField(null)} 
          saveStatus={editingField === 'businessStructure' ? saveStatus : 'idle'} 
        />

        <ProfileFieldDropdownBalanced 
          label="Industry" 
          value={profileData.industry} 
          options={getIndustryOptions()} 
          isRequired={true} 
          isEditing={editingField === 'industry'} 
          onEdit={() => setEditingField('industry')} 
          onSave={value => handleFieldEdit('industry', value)} 
          onCancel={() => setEditingField(null)} 
          saveStatus={editingField === 'industry' ? saveStatus : 'idle'} 
        />

        <ProfileFieldBalanced 
          label="Website" 
          value={profileData.website} 
          isRequired={false} 
          isEditing={editingField === 'website'} 
          onEdit={() => setEditingField('website')} 
          onSave={value => handleFieldEdit('website', value)} 
          onCancel={() => setEditingField(null)} 
          placeholder="https://" 
          saveStatus={editingField === 'website' ? saveStatus : 'idle'} 
        />
      </div>

      <SaveStatusIndicator status={saveStatus} />
    </div>;
}